import { useEffect, useRef } from 'react';

type Options = {
  // Avoid toggling when typing in inputs/textareas/contentEditable
  ignoreTextInputs?: boolean;
  // Also allow ⌘/Cmd on macOS
  supportMetaKey?: boolean;
  // Debounce window (ms) to avoid double fires from key repeat
  debounceMs?: number;
};

const isTextEditable = (el: Element | null) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || (el as HTMLElement).isContentEditable) return true;
  return false;
};

export function useDevHotkeys(
  onToggle: () => void,
  opts: Options = { ignoreTextInputs: true, supportMetaKey: true, debounceMs: 250 },
) {
  const lastToggleRef = useRef(0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const now = Date.now();
      if (now - lastToggleRef.current < (opts.debounceMs ?? 250)) return;

      // Respect focus in text inputs if requested
      if (opts.ignoreTextInputs && isTextEditable(document.activeElement)) return;

      const ctrl = e.ctrlKey;
      const shift = e.shiftKey;
      const meta = e.metaKey; // ⌘ on macOS
      const allowMeta = !!opts.supportMetaKey;

      // Ctrl+Shift+Q (or Cmd+Shift+Q on macOS when allowed)
      const keyMatches = e.code === 'KeyQ' || e.key?.toLowerCase() === 'q';
      const chordMatches = (ctrl && shift) || (allowMeta && meta && shift);

      if (keyMatches && chordMatches) {
        e.preventDefault();
        lastToggleRef.current = now;
        onToggle();
      }
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true } as any);
  }, [onToggle, opts.ignoreTextInputs, opts.supportMetaKey, opts.debounceMs]);
}
